import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import moment from "moment";
import { formatUtcDateTimeInLabTimeZone } from "Utils/Common/LabTimeZone";
import React, { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchDateRangePicker from "../../../Shared/Common/DatePicker/SearchDateRangePicker";
import { SortingTypeI } from "../../../Utils/consts";
import {
  extractBarcodeRecordsFromSupplyItem,
  extractBarcodeRecords,
  getRowAction,
  getStatusClassName,
  getStatusText,
  initialSearchQuery,
  isBarcodeGeneratedStatus,
  mergeBarcodeRecords,
} from "./helpers";
import { useOncologyOrderDetail } from "./hooks";
import {
  BarcodeRecord,
  OrderAction,
  SupplyOrder,
  SupplyOrderItem,
} from "./types";
import Status from "Shared/Common/Status";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import useLang from "Shared/hooks/useLanguage";
import { AddIcon, ArrowDown, ArrowUp, RemoveICon } from "Shared/Icons";
import { PermissionObject } from "Shared/Common/Permissions/PermissionComponent";

export type ListMode = "new" | "shipped" | "rejected";

type PermissionState = {
  Reducer?: {
    selectedTenantInfo?: {
      infomationOfLoggedUser?: {
        permissions?: PermissionObject[];
      };
    };
  };
};

const hasOncologyOrderPermission = (
  permissions: PermissionObject[],
  action: string
) =>
  permissions.some(
    (permission) =>
      permission.subject.replace(/\n/g, "").toLowerCase() ===
        "Manage Order (Oncology)".toLowerCase() &&
      permission.moduleName.replace(/\n/g, "").toLowerCase() ===
        "Supply Management".toLowerCase() &&
      permission.action.replace(/\n/g, "").toLowerCase() ===
        action.toLowerCase()
  );

type OrderListTableProps = {
  actionLookup: OrderAction[];
  expandedOrderId: number | null;
  loading: boolean;
  mode: ListMode;
  orderActionLoading: boolean;
  orderActionRowId: number | null;
  pendingBarcodes: Record<number, BarcodeRecord[]>;
  printBarcodeLoading: boolean;
  printBarcodeRowId: number | null;
  rows: SupplyOrder[];
  searchRequest: typeof initialSearchQuery;
  selectedIds: number[];
  selectAll: boolean;
  sort: SortingTypeI;
  onOrderAction: (row: SupplyOrder, action: OrderAction) => void;
  onPrintBarcode: (record: BarcodeRecord) => void;
  onPrintBarcodes: (records: BarcodeRecord[]) => void;
  onPrintItemBarcodes: (order: SupplyOrder, item: SupplyOrderItem) => void;
  onPrintOrderBarcodes: (row: SupplyOrder) => void;
  onSearchChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  onSearchKeyPress: (e: React.KeyboardEvent) => void;
  onSelectAll: (checked: boolean) => void;
  onSelectRow: (checked: boolean, id: number) => void;
  onSort: (columnName: string) => void;
  onToggleExpand: (row: SupplyOrder) => void;
};

function OrderListTable({
  actionLookup,
  expandedOrderId,
  loading,
  mode,
  orderActionLoading,
  orderActionRowId,
  pendingBarcodes,
  printBarcodeLoading,
  printBarcodeRowId,
  rows,
  searchRequest,
  selectedIds,
  selectAll,
  sort,
  onOrderAction,
  onPrintBarcode,
  onPrintBarcodes,
  onPrintItemBarcodes,
  onPrintOrderBarcodes,
  onSearchChange,
  onSearchKeyPress,
  onSelectAll,
  onSelectRow,
  onSort,
  onToggleExpand,
}: OrderListTableProps) {
  const permissions = useSelector(
    (state: PermissionState) =>
      state.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.permissions ??
      []
  );
  const canUpdateOrders = hasOncologyOrderPermission(permissions, "Update");
  const canUsePrinter = hasOncologyOrderPermission(permissions, "Printer");

  return (
    <div className="table_bordered overflow-hidden">
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 100px)",
          "&::-webkit-scrollbar": { width: 7 },
          "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
          "&:hover": {
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "var(--kt-gray-400)",
              borderRadius: 2,
            },
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--kt-gray-400)",
            borderRadius: 2,
          },
        }}
        className="shadow-none"
      >
        <Table
          aria-label="oncology supply orders"
          className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
        >
          <OrderTableHead
            mode={mode}
            rows={rows}
            searchRequest={searchRequest}
            selectAll={selectAll}
            showActionColumn={canUpdateOrders}
            sort={sort}
            onSearchChange={onSearchChange}
            onSearchKeyPress={onSearchKeyPress}
            onSelectAll={onSelectAll}
            onSort={onSort}
          />
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={canUpdateOrders ? 10 : 9}>
                  <Loader />
                </TableCell>
              </TableRow>
            ) : rows.length ? (
              rows.map((row) => (
                <OrderRow
                  key={row.id}
                  row={row}
                  mode={mode}
                  selected={selectedIds.includes(row.id)}
                  showActionColumn={canUpdateOrders}
                  canUsePrinter={canUsePrinter}
                  expanded={expandedOrderId === row.id}
                  action={getRowAction(row, actionLookup)}
                  actionLoading={
                    orderActionLoading && orderActionRowId === row.id
                  }
                  pendingBarcodes={pendingBarcodes[row.id] ?? []}
                  printBarcodeLoading={
                    printBarcodeLoading && printBarcodeRowId === row.id
                  }
                  onOrderAction={onOrderAction}
                  onPrintBarcode={onPrintBarcode}
                  onPrintBarcodes={onPrintBarcodes}
                  onPrintItemBarcodes={onPrintItemBarcodes}
                  onPrintOrderBarcodes={onPrintOrderBarcodes}
                  onSelectRow={onSelectRow}
                  onToggleExpand={onToggleExpand}
                />
              ))
            ) : (
              <NoRecord />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

function SortHeader({
  column,
  label,
  sort,
  onSort,
}: {
  column: string;
  label: string;
  sort: SortingTypeI;
  onSort: (columnName: string) => void;
}) {
  const { t } = useLang();

  return (
    <div
      onClick={() => onSort(column)}
      className="d-flex justify-content-between cursor-pointer"
    >
      <div style={{ width: "max-content" }}>{t(label)}</div>
      <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
        <ArrowUp
          CustomeClass={`${
            sort.sortingOrder === "desc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
        <ArrowDown
          CustomeClass={`${
            sort.sortingOrder === "asc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
          } p-0 m-0`}
        />
      </div>
    </div>
  );
}

function OrderTableHead({
  mode,
  rows,
  searchRequest,
  selectAll,
  showActionColumn,
  sort,
  onSearchChange,
  onSearchKeyPress,
  onSelectAll,
  onSort,
}: {
  mode: ListMode;
  rows: SupplyOrder[];
  searchRequest: typeof initialSearchQuery;
  selectAll: boolean;
  showActionColumn: boolean;
  sort: SortingTypeI;
  onSearchChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  onSearchKeyPress: (e: React.KeyboardEvent) => void;
  onSelectAll: (checked: boolean) => void;
  onSort: (columnName: string) => void;
}) {
  const { t } = useLang();

  return (
    <TableHead>
      <TableRow className="h-40px">
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        {showActionColumn && <TableCell></TableCell>}
        <TableCell>
          <input
            id={`OncologyManageOrder${mode}SearchFacilityName`}
            type="text"
            name="facilityName"
            className="form-control bg-white h-30px rounded-2 fs-8 mb-lg-0"
            placeholder={t("Search ...")}
            value={searchRequest.facilityName}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyPress}
          />
        </TableCell>
        <TableCell>
          <input
            id={`OncologyManageOrder${mode}SearchCreatedBy`}
            type="text"
            name="createdBy"
            className="form-control bg-white h-30px rounded-2 fs-8 mb-lg-0"
            placeholder={t("Search ...")}
            value={searchRequest.createdBy}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyPress}
          />
        </TableCell>
        <TableCell>
          <input
            id={`OncologyManageOrder${mode}SearchRepresentativeName`}
            type="text"
            name="representativeName"
            className="form-control bg-white h-30px rounded-2 fs-8 mb-lg-0 min-w-200px"
            placeholder={t("Search ...")}
            value={searchRequest.representativeName}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyPress}
          />
        </TableCell>
        <TableCell>
          <SearchDateRangePicker
            id={`OncologyManageOrder${mode}SearchDateOfRequest`}
            name="dateofRequest"
            value={searchRequest.dateofRequest ?? ""}
            onChange={onSearchChange}
          />
        </TableCell>
        <TableCell>
          <input
            id={`OncologyManageOrder${mode}SearchApprovedBy`}
            type="text"
            name="approvedBy"
            className="form-control bg-white h-30px rounded-2 fs-8 mb-lg-0"
            placeholder={t("Search ...")}
            value={searchRequest.approvedBy}
            onChange={onSearchChange}
            onKeyDown={onSearchKeyPress}
          />
        </TableCell>
        <TableCell></TableCell>
      </TableRow>
      <TableRow className="h-30px">
        <TableCell className="w-40px min-w-40px"></TableCell>
        <TableCell className="w-20px min-w-20px">
          <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
            <input
              id={`OncologyManageOrder${mode}CheckAll`}
              className="form-check-input"
              type="checkbox"
              onChange={(e) => onSelectAll(e.target.checked)}
              checked={selectAll && rows.length > 0}
            />
          </label>
        </TableCell>
        <TableCell className="min-w-120px">
          <SortHeader
            label="Status"
            column="status"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        {showActionColumn && (
          <TableCell className="min-w-150px">
            <div style={{ width: "max-content" }}>{t("Action")}</div>
          </TableCell>
        )}
        <TableCell sx={{ width: "max-content" }}>
          <SortHeader
            label="Shipping To"
            column="facilityName"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        <TableCell sx={{ width: "max-content" }}>
          <SortHeader
            label="Requested By"
            column="createdBy"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        <TableCell>
          <SortHeader
            label="Representative Name"
            column="representativeName"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        <TableCell sx={{ width: "max-content" }}>
          <SortHeader
            label="Requested Date"
            column="dateofRequest"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        <TableCell sx={{ width: "max-content" }}>
          <SortHeader
            label="Approved By"
            column="approvedBy"
            sort={sort}
            onSort={onSort}
          />
        </TableCell>
        <TableCell sx={{ width: "max-content" }}>
          <div style={{ width: "max-content" }}>{t("View Order")}</div>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function OrderRow({
  row,
  mode,
  selected,
  expanded,
  action,
  actionLoading,
  showActionColumn,
  canUsePrinter,
  pendingBarcodes,
  printBarcodeLoading,
  onOrderAction,
  onPrintBarcode,
  onPrintBarcodes,
  onPrintItemBarcodes,
  onPrintOrderBarcodes,
  onSelectRow,
  onToggleExpand,
}: {
  row: SupplyOrder;
  mode: ListMode;
  selected: boolean;
  expanded: boolean;
  action?: OrderAction | null;
  actionLoading: boolean;
  showActionColumn: boolean;
  canUsePrinter: boolean;
  pendingBarcodes: BarcodeRecord[];
  printBarcodeLoading: boolean;
  onOrderAction: (row: SupplyOrder, action: OrderAction) => void;
  onPrintBarcode: (record: BarcodeRecord) => void;
  onPrintBarcodes: (records: BarcodeRecord[]) => void;
  onPrintItemBarcodes: (order: SupplyOrder, item: SupplyOrderItem) => void;
  onPrintOrderBarcodes: (row: SupplyOrder) => void;
  onSelectRow: (checked: boolean, id: number) => void;
  onToggleExpand: (row: SupplyOrder) => void;
}) {
  const { t } = useLang();
  const navigate = useNavigate();
  const labTimeZone = useSelector(
    (state: any) => state?.Reducer?.selectedTenantInfo?.labTimeZone
  );
  const { data: detailOrder, isLoading: detailLoading } =
    useOncologyOrderDetail(row.id, expanded);

  const statusText = getStatusText(row);
  const showPrintAction = isBarcodeGeneratedStatus(row);
  const barcodes = useMemo(() => {
    const detailOrderBarcodes = detailOrder?.barcodes;
    const detailOrderBarCodes = detailOrder?.barCodes;
    const detailBarcodes = Array.isArray(detailOrderBarcodes)
      ? detailOrderBarcodes
      : Array.isArray(detailOrderBarCodes)
        ? detailOrderBarCodes
        : [];

    return mergeBarcodeRecords(
      pendingBarcodes,
      extractBarcodeRecords(detailBarcodes)
    );
  }, [detailOrder, pendingBarcodes]);

  return (
    <Fragment>
      <TableRow className="h-30px">
        <TableCell align="center">
          <IconButton
            aria-label="expand row"
            size="small"
            className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px"
          >
            {expanded ? (
              <button
                id={`OncologyManageOrder${mode}Expand_${row.id}`}
                className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                type="button"
                onClick={() => onToggleExpand(row)}
              >
                <RemoveICon />
              </button>
            ) : (
              <button
                id={`OncologyManageOrder${mode}Expand_${row.id}`}
                className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                type="button"
                onClick={() => onToggleExpand(row)}
              >
                <AddIcon />
              </button>
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
            <input
              id={`OncologyManageOrder${mode}CheckBox_${row.id}`}
              className="form-check-input"
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelectRow(e.target.checked, row.id)}
            />
          </label>
        </TableCell>
        <TableCell
          id={`OncologyManageOrder${mode}Status_${row.id}`}
          sx={{ width: "max-content", textAlign: "center" }}
        >
          <Status cusText={statusText} cusClassName={getStatusClassName(row)} />
        </TableCell>
        {showActionColumn && (
          <TableCell align="center">
            {action ? (
              <button
                id={`OncologyManageOrder${mode}Action_${row.id}`}
                className="badge badge-pill badge-primary py-3 px-4 border-0 fw-400 fa-1x text-light"
                type="button"
                onClick={() => onOrderAction(row, action)}
                disabled={actionLoading}
              >
                {actionLoading ? t("Processing...") : t(action.label)}
              </button>
            ) : showPrintAction && canUsePrinter ? (
              <button
                id={`OncologyManageOrder${mode}PrintBarcode_${row.id}`}
                className="badge badge-pill badge-warning py-3 px-4 border-0 fw-400 fa-1x text-light"
                type="button"
                onClick={() => onPrintOrderBarcodes(row)}
                disabled={printBarcodeLoading}
              >
                {printBarcodeLoading ? t("Printing...") : t("Print Barcode")}
              </button>
            ) : null}
          </TableCell>
        )}
        <TableCell
          id={`OncologyManageOrder${mode}FacilityName_${row.id}`}
          sx={{ width: "max-content" }}
        >
          {row.facilityName}
        </TableCell>
        <TableCell id={`OncologyManageOrder${mode}CreatedBy_${row.id}`}>
          {row.createdBy}
        </TableCell>
        <TableCell
          id={`OncologyManageOrder${mode}RepresentativeName_${row.id}`}
          sx={{ width: "max-content" }}
        >
          {row.representativeName}
        </TableCell>
        <TableCell
          id={`OncologyManageOrder${mode}DateOfRequest_${row.id}`}
          sx={{ width: "max-content" }}
        >
          {row.dateofRequest
            ? (formatUtcDateTimeInLabTimeZone(
                row.dateofRequest,
                labTimeZone,
                "MM-dd-yyyy"
              ) ?? moment(row.dateofRequest).format("MM-DD-YYYY"))
            : ""}
        </TableCell>
        <TableCell id={`OncologyManageOrder${mode}ApprovedBy_${row.id}`}>
          {row.approvedBy}
        </TableCell>
        <TableCell
          id={`OncologyManageOrder${mode}ViewOrder_${row.id}`}
          align="center"
          sx={{ width: "max-content" }}
        >
          <button
            id={`OncologyManageOrder${mode}ViewOrderButton_${row.id}`}
            className="badge badge-pill badge-warning py-3 px-4 border-0 fw-400 fa-1x text-light"
            onClick={() =>
              navigate("/supply-order-oncology-view", {
                state: { id: row.id },
              })
            }
          >
            <span>{t("View Order")}</span>
          </button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={showActionColumn ? 10 : 9} className="p-0 border-0">
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <ExpandedOrderDetail
                loading={detailLoading}
                order={detailOrder ?? undefined}
                barcodes={barcodes}
                canUsePrinter={canUsePrinter}
                onPrintBarcode={onPrintBarcode}
                onPrintBarcodes={onPrintBarcodes}
                onPrintItemBarcodes={onPrintItemBarcodes}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

function ExpandedOrderDetail({
  loading,
  order,
  barcodes,
  canUsePrinter,
  onPrintBarcode,
  onPrintBarcodes,
  onPrintItemBarcodes,
}: {
  loading?: boolean;
  order?: SupplyOrder;
  barcodes: BarcodeRecord[];
  canUsePrinter: boolean;
  onPrintBarcode: (record: BarcodeRecord) => void;
  onPrintBarcodes: (records: BarcodeRecord[]) => void;
  onPrintItemBarcodes: (order: SupplyOrder, item: SupplyOrderItem) => void;
}) {
  const { t } = useLang();
  const items = order?.supplyOrderItems ?? [];
  const barcodeGroups = useMemo(
    () => buildBarcodeGroups(items, barcodes, t("Generated Barcodes")),
    [barcodes, items, t]
  );

  return (
    <div className="bg-white px-6 py-5 rounded">
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="m-0 fw-bold">{t("Supplies Information")}</h6>
          </div>
          <div className="table_bordered overflow-hidden mb-5">
            <Table className="table table-bordered table-bg table-head-custom table-vertical-center plate-mapping-table border-0 mb-0">
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    backgroundColor: "#FAFAD2",
                    color: "#070910",
                    fontWeight: 600,
                    borderBottom: 0,
                  },
                }}
              >
                <TableRow>
                  <TableCell>{t("Item Type")}</TableCell>
                  <TableCell>{t("Item")}</TableCell>
                  <TableCell>{t("Description")}</TableCell>
                  <TableCell>{t("QTY Requested")}</TableCell>
                  <TableCell>{t("QTY Approved")}</TableCell>
                  <TableCell>{t("Special Request")}</TableCell>
                  <TableCell>{t("Comments")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length ? (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.itemType}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.itemDescription}</TableCell>
                      <TableCell>{item.orderQuantityRequested}</TableCell>
                      <TableCell>{item.orderQuantityApproved}</TableCell>
                      <TableCell>{item.specialRequest}</TableCell>
                      <TableCell>{item.comments}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <NoRecord />
                )}
              </TableBody>
            </Table>
          </div>

          {barcodeGroups.length > 0 && (
            <>
              <div className="d-flex align-items-center mb-3">
                <h6 className="m-0 fw-bold">{t("Barcodes")}</h6>
              </div>
              {barcodeGroups.map((group) => (
                <BarcodeGroupTable
                  key={group.key}
                  group={group}
                  order={order}
                  canUsePrinter={canUsePrinter}
                  onPrintBarcode={onPrintBarcode}
                  onPrintBarcodes={onPrintBarcodes}
                  onPrintItemBarcodes={onPrintItemBarcodes}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

type BarcodeGroup = {
  key: string;
  item?: SupplyOrderItem;
  title: string;
  subtitle?: string;
  records: BarcodeRecord[];
};

const buildBarcodeGroups = (
  items: SupplyOrderItem[],
  fallbackBarcodes: BarcodeRecord[],
  fallbackTitle: string
): BarcodeGroup[] => {
  const itemGroups = items
    .map((item, index) => {
      const records = extractBarcodeRecordsFromSupplyItem(item);
      return {
        key: `${item.id ?? index}`,
        item,
        title: item.itemName || fallbackTitle,
        subtitle: item.itemType,
        records,
      };
    })
    .filter((group) => group.records.length > 0);

  if (itemGroups.length > 0) return itemGroups;
  if (!fallbackBarcodes.length) return [];

  return [
    {
      key: "generated-barcodes",
      title: fallbackTitle,
      records: fallbackBarcodes,
    },
  ];
};

function BarcodeGroupTable({
  group,
  order,
  canUsePrinter,
  onPrintBarcode,
  onPrintBarcodes,
  onPrintItemBarcodes,
}: {
  group: BarcodeGroup;
  order?: SupplyOrder;
  canUsePrinter: boolean;
  onPrintBarcode: (record: BarcodeRecord) => void;
  onPrintBarcodes: (records: BarcodeRecord[]) => void;
  onPrintItemBarcodes: (order: SupplyOrder, item: SupplyOrderItem) => void;
}) {
  const { t } = useLang();

  return (
    <div className="table_bordered overflow-hidden mb-4">
      <div className="d-flex justify-content-between align-items-center gap-3 px-4 py-3 bg-light border-bottom flex-wrap">
        <div>
          <div className="fw-bold">{group.title}</div>
          <div className="text-muted fs-8">
            {group.subtitle ? `${group.subtitle} | ` : ""}
            {t("Total Barcodes")}: {group.records.length}
          </div>
        </div>
        {canUsePrinter && (
          <button
            type="button"
            className="badge badge-pill badge-warning py-3 px-4 border-0 fw-400 fa-1x text-light"
            onClick={() => {
              if (order && group.item) {
                onPrintItemBarcodes(order, group.item);
                return;
              }

              onPrintBarcodes(group.records);
            }}
          >
            {t("Bulk Print")}
          </button>
        )}
      </div>
      <Table className="table table-bordered table-bg table-head-custom table-vertical-center plate-mapping-table border-0 mb-0">
        <TableHead
          sx={{
            "& .MuiTableCell-head": {
              backgroundColor: "#FAFAD2",
              color: "#070910",
              fontWeight: 600,
              borderBottom: 0,
            },
          }}
        >
          <TableRow>
            <TableCell>{t("Barcode")}</TableCell>
            <TableCell>{t("Status")}</TableCell>
            <TableCell>{t("Generated Date")}</TableCell>
            {canUsePrinter && (
              <TableCell align="center">{t("Action")}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {group.records.map((barcode, index) => (
            <TableRow key={`${group.key}-${barcode.value}-${index}`}>
              <TableCell>{barcode.value}</TableCell>
              <TableCell>{barcode.status}</TableCell>
              <TableCell>
                {barcode.generatedDate
                  ? moment(barcode.generatedDate).format("MM-DD-YYYY hh:mm A")
                  : ""}
              </TableCell>
              {canUsePrinter && (
                <TableCell align="center">
                  <button
                    type="button"
                    className="badge badge-pill badge-warning py-3 px-4 border-0 fw-400 fa-1x text-light"
                    onClick={() =>
                      onPrintBarcode({
                        ...barcode,
                        itemName:
                          group.item?.itemName ?? barcode.itemName ?? "",
                      })
                    }
                  >
                    {t("Print Barcode")}
                  </button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default OrderListTable;
